import { useRef, useState } from 'react';
import {
  DraftSaveFlushError,
  useUpdateTemplateVersion,
  type DraftSaveQueue,
  type SupabaseClient,
  type WebsiteNotCarriedSlot,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useWebsiteT } from '../hooks/use-website-t';
import { isStaleDraft, stageErrorKey } from '../lib/issue-messages';
import { slotFieldLabel } from '../lib/region-labels';

export interface UpdateTemplateDialogProps {
  readonly client: SupabaseClient;
  readonly storeId: string;
  readonly templateId: string;
  /**
   * Epic #228 P4 (PD10): the template's auto-save queue. The update waits for
   * the queue to save, runs on the queue's base and hands the new head back.
   */
  readonly queue: DraftSaveQueue;
  /** Asks the server for this template's draft again (the conflict action). */
  readonly reloadState: () => Promise<unknown>;
  /** Cancelled, closed, or done. The editor already shows the updated draft. */
  readonly onClose: () => void;
  readonly newMutationId?: () => string;
}

type Stage = 'confirm' | 'done';

/**
 * Epic #228 P3 "Update template": moves this template's draft to the newest
 * version of the design. A question with Cancel comes first; nothing is
 * published. After the update the editor shows the new draft (the shared hook
 * refreshes it before the mutation settles) and this dialog says what, if
 * anything, had no place in the new version.
 */
export function UpdateTemplateDialog({
  client,
  storeId,
  templateId,
  queue,
  reloadState,
  onClose,
  newMutationId = () => crypto.randomUUID(),
}: UpdateTemplateDialogProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const update = useUpdateTemplateVersion(client, storeId);
  const [stage, setStage] = useState<Stage>('confirm');
  const [notCarried, setNotCarried] = useState<readonly WebsiteNotCarriedSlot[]>([]);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [running, setRunning] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [flushFailed, setFlushFailed] = useState(false);
  // A retry of the same update (same draft) reuses its id: the server replays the first result.
  const mutationRef = useRef<{ readonly id: string; readonly head: string } | null>(null);
  const busy = running || update.isPending || reloading;

  const confirm = async (): Promise<void> => {
    setErrorKey(null);
    setConflict(false);
    setFlushFailed(false);
    setRunning(true);
    try {
      const result = await queue.runExclusive(async (base) => {
        const attempt =
          mutationRef.current?.head === base
            ? mutationRef.current
            : { id: newMutationId(), head: base };
        mutationRef.current = attempt;
        const updated = await update.mutateAsync({
          templateId,
          expectedHeadRevisionId: base,
          clientMutationId: attempt.id,
        });
        return {
          result: updated,
          head: {
            revisionId: updated.head?.revisionId ?? base,
            slots: updated.draft?.document.slots,
          },
        };
      });
      mutationRef.current = null;
      setNotCarried(result.notCarriedOver);
      setStage('done');
    } catch (error) {
      if (error instanceof DraftSaveFlushError) {
        setFlushFailed(true);
        setErrorKey('admin.websiteTemplates.publish.flushFirst');
      } else if (isStaleDraft(error)) {
        setConflict(true);
      } else {
        setErrorKey(stageErrorKey(error));
      }
    } finally {
      setRunning(false);
    }
  };

  // The draft changed elsewhere: take the newer draft (the queue's base too).
  const reload = async (): Promise<void> => {
    setReloading(true);
    try {
      const reloaded = await queue.reload();
      if (!reloaded.ok) {
        // The newer head could not be taken (fetch failed) or the merchant's
        // queued edits could not be saved on top of it: stay, say so, keep Reload.
        setErrorKey(
          queue.getSnapshot().hasFailedPayload
            ? 'admin.websiteTemplates.publish.flushFirst'
            : 'admin.websiteTemplates.errors.generic',
        );
        return;
      }
      await reloadState();
      onClose();
    } catch {
      setErrorKey('admin.websiteTemplates.errors.generic');
    } finally {
      setReloading(false);
    }
  };

  const done = stage === 'done';

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <DialogContent data-testid="update-template-dialog" data-stage={stage}>
        <DialogHeader>
          <DialogTitle>
            {done
              ? t('admin.websiteTemplates.editor.updateTemplateDone')
              : t('admin.websiteTemplates.editor.updateTemplate')}
          </DialogTitle>
          <DialogDescription>
            {done
              ? notCarried.length === 0
                ? t('admin.websiteTemplates.editor.updateTemplateAllKept')
                : t('admin.websiteTemplates.stage.notCarriedBody')
              : t('admin.websiteTemplates.editor.updateTemplateHint')}
          </DialogDescription>
        </DialogHeader>

        {done && notCarried.length > 0 ? (
          <ul className="space-y-1 text-sm" data-testid="not-carried-list">
            {notCarried.map((slot) => (
              <li key={slot.slotId}>{slotFieldLabel(t, slot)}</li>
            ))}
          </ul>
        ) : null}

        {conflict ? (
          <p
            role="alert"
            data-testid="update-template-conflict"
            className="text-destructive text-sm"
          >
            {t('admin.websiteTemplates.dialog.conflictTitle')}
          </p>
        ) : null}
        {errorKey === null ? null : (
          <p role="alert" data-testid="update-template-error" className="text-destructive text-sm">
            {t(errorKey)}
          </p>
        )}

        <DialogFooter>
          {done ? (
            <Button type="button" onClick={onClose}>
              {t('admin.websiteTemplates.common.close')}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                data-testid="update-template-cancel"
                onClick={onClose}
              >
                {t('admin.websiteTemplates.common.cancel')}
              </Button>
              {conflict ? (
                <Button
                  type="button"
                  disabled={busy}
                  data-testid="update-template-reload"
                  onClick={() => void reload()}
                >
                  {t('admin.websiteTemplates.dialog.reloadDraft')}
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={busy}
                  data-testid="update-template-confirm"
                  onClick={() => void confirm()}
                >
                  {busy
                    ? t('admin.websiteTemplates.editor.updating')
                    : flushFailed
                      ? t('admin.websiteTemplates.common.retry')
                      : t('admin.websiteTemplates.editor.updateTemplate')}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
